/**
 * Where what's shared to Revisa goes (the app's share extension shows this page, alone: the `share` fragment): what's
 * being added, the space, where in it (its triage area, as first chosen; its items; one of its folders, searched as
 * "Move to…" searches them) and what it is (one of the space's entities). The app adds it: the page only says, as it
 * changes, what's chosen (`shareChoice`).
 */
import { useEffect, useState } from "preact/hooks";
import type { SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { useEntities, useSpace } from "../lib/data.ts";
import { useFolderIndex } from "../lib/folders.ts";
import { ignore } from "../lib/ignore.ts";
import { post, sharing } from "../lib/native.ts";
import { homeSpace, mySpaces, rememberSpace } from "../lib/state.ts";
import { Combobox } from "../ui/Combobox.tsx";
import { Field, Select } from "../ui/form.tsx";
import { LinkIcon, NAV_ICONS, NewDocumentIcon } from "../ui/icons.tsx";
import { Spinner } from "../ui/Spinner.tsx";
import { folderOffers, ROOT } from "./folders/offers.tsx";

/** Where in the space: its triage area (none), its items in no folder, or a folder (its id). */
const TRIAGE = "triage";

/** What's being added, by name. */
function Shared() {
  const list = sharing();
  return (
    <ul class="flex flex-col gap-2 text-sm">
      {list.map((s) => (
        <li key={s.label} class="flex min-w-0 items-center gap-2">
          {s.kind === "page" ? <LinkIcon size={16} /> : <NewDocumentIcon size={16} />}
          <span class="truncate">{s.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** Where in `space` and as what, said to the app as it's chosen. */
function Destination({ space }: { space: SpaceOut }) {
  const index = useFolderIndex(space);
  const entities = useEntities(space);
  const [where, setWhere] = useState(TRIAGE);
  const [entity, setEntity] = useState("");
  useEffect(() => {
    const folder = where === TRIAGE ? null : where === ROOT ? "/" : where;
    post("shareChoice", { space: space.id, folder, entity: entity || null }).catch(ignore);
  }, [space.id, where, entity]);
  const triage = {
    value: TRIAGE,
    label: t("nav.triage"),
    hint: t("share.triageHint"),
    icon: <NAV_ICONS.triage size={14} />,
  };
  return (
    <>
      <Field group={true} label={t("share.where")}>
        <Combobox
          key={index.data ? "ready" : "loading"}
          label={t("share.where")}
          placeholder={t("folders.search")}
          inline={true}
          picked={where}
          search={(q) => {
            const folders = folderOffers(index.data, space, q, []);
            return Promise.resolve(q.trim() ? folders : [triage, ...folders]);
          }}
          onSelect={(o) => setWhere(o.value)}
        />
      </Field>
      {(entities.data ?? []).length > 0 ? (
        <Field label={t("items.entity")}>
          <Select
            value={entity}
            onValue={setEntity}
            options={[
              { value: "", label: t("items.noEntity") },
              ...(entities.data ?? []).map((e) => ({ value: e.id, label: e.name })),
            ]}
          />
        </Field>
      ) : null}
    </>
  );
}

export function SharePage() {
  const spaces = mySpaces();
  const [id, setId] = useState(homeSpace() ?? "");
  const space = useSpace(id);
  if (!id) {
    return null;
  }
  return (
    <div class="flex flex-col gap-5 p-4">
      <Shared />
      {spaces.length > 1 ? (
        <Field label={t("share.space")}>
          <Select
            value={id}
            onValue={(v) => {
              setId(v);
              rememberSpace(v);
            }}
            options={spaces.map((s) => ({ value: s.space, label: s.name, group: s.org.name }))}
          />
        </Field>
      ) : null}
      {space.data?.id === id ? (
        <Destination key={id} space={space.data} />
      ) : space.error ? (
        <p class="text-sm text-red-700">{t("share.failed")}</p>
      ) : (
        <Spinner />
      )}
    </div>
  );
}
